export type DepotProfileType = 'google' | 'tripadvisor';

export interface DepotProfileSiteAssignmentPayload {
    profile_uuid: string;
    profile_type: DepotProfileType;
    target_company_uuid: string;
    target_site_uuid: string;
}

export type DepotProfileSiteAssignmentRemovedCompany = Record<string, unknown>;

export interface DepotProfileSiteAssignmentSuccessResponse {
    company_uuid: string;
    company_name: string;
    site_name: string;
    site_uuid: string;
    profile_uuid: string;
    removed_company: DepotProfileSiteAssignmentRemovedCompany | null;
}

export interface DepotProfileSiteAssignmentNotFoundResponse {
    message: string;
}

export interface DepotProfileSiteAssignmentErrorResponse {
    message: unknown;
}

export type DepotProfileSiteAssignmentResponse =
    | DepotProfileSiteAssignmentSuccessResponse
    | DepotProfileSiteAssignmentNotFoundResponse
    | DepotProfileSiteAssignmentErrorResponse;

/**
 * Body of `POST /sites/remove-profile`. No company identifier: the endpoint
 * resolves the owning company from the site, and the channel is matched on the
 * profile id alone.
 */
export interface DepotProfileSiteRemovalPayload {
    profile_uuid: string;
    site_uuid: string;
}

/** The 200 body, naming everything the endpoint resolved on the way through. */
export interface DepotProfileSiteRemovalSuccessResponse {
    profile_uuid: string;
    site_uuid: string;
    site_name: string;
    company_uuid: string;
    company_name: string;
}

/**
 * Outcome of `POST /sites/remove-profile`.
 *
 * Carries `status` because the four responses mean four different things to the
 * operator and only two of them may be retried, which a boolean cannot express.
 * `data` is the 200 body and is absent otherwise, so the success copy can name
 * the site and company the endpoint itself resolved rather than what was sent.
 * `status` is absent when the request never reached the endpoint.
 */
export interface DepotProfileSiteRemovalOutcome {
    ok: boolean;
    status?: number;
    message?: string;
    data?: DepotProfileSiteRemovalSuccessResponse;
}

/**
 * Outcome of `POST /sites/reassign-site`. Every response from that endpoint is a
 * `{ message }` body, and the message is worth showing: the 200 names the old
 * company, the new company and the site, and the 400 explains why the move was
 * refused. `status` is absent when the request never reached the endpoint.
 */
export interface DepotSiteReassignmentOutcome {
    ok: boolean;
    status?: number;
    message?: string;
}

export interface DepotApiRouteResponse<TResponse = unknown> {
    data: TResponse;
    status: number;
    statusText: string;
    headers: unknown;
    config: unknown;
}

/**
 * What a profile-ownership sync covers.
 *
 * The endpoint's three selectors are mutually exclusive, so this is a union
 * rather than three optional fields: sending two of them is unrepresentable
 * instead of being something to validate at runtime.
 *
 * The whole estate is the ABSENCE of a scope — `undefined` — mirroring the wire
 * format, where an omitted body means everything.
 */
export type OwnershipScope =
    | { kind: 'profile'; uuid: string }
    | { kind: 'site'; uuid: string }
    | { kind: 'company'; uuid: string };

/** Request body for a scoped sync. Exactly one field is ever set. */
export interface SyncProfileOwnershipRequest {
    profile_uuid?: string;
    site_uuid?: string;
    company_uuid?: string;
}

export const isDepotProfileSiteAssignmentSuccessResponse = (
    response: DepotProfileSiteAssignmentResponse | null | undefined
): response is DepotProfileSiteAssignmentSuccessResponse => {
    return Boolean(
        response &&
            typeof response === 'object' &&
            'company_uuid' in response &&
            'site_uuid' in response &&
            'profile_uuid' in response
    );
};

/**
 * A single row in the global site search results.
 *
 * `site_name` and `place_id` are empty strings rather than absent when the site
 * has neither, so both are always present and never null.
 */
export interface SiteSearchRow {
    company_uuid: string;
    company_name: string;
    site_uuid: string;
    site_name: string;
    place_id: string;
    sectors: string[];
}

/** Paged envelope returned by the site search. */
export interface SitesSearchResponse {
    data: SiteSearchRow[];
    previous_page_token: string | null;
    next_page_token: string | null;
    has_more: boolean;
    total_count: number;
    total_pages: number;
}

/**
 * Request body accepted by `POST /company/sites/search`.
 *
 * `page_token` is accepted by the endpoint and takes precedence over `page`, but
 * this client never sends it: it pages numerically, so `page` is the single
 * source of truth for where the operator is. Declared so a future jump control
 * has somewhere to go.
 */
export interface SitesSearchRequest {
    name?: string;
    place_id?: string;
    company_uuid?: string;
    page?: string | number;
    per_page?: string | number;
    page_token?: string;
}
